import { useEffect } from 'react';

import ActionIconButton from '@components/buttons/ActionIconButton';
import Segment, { ButtonProps } from '@components/buttons/Segment';
import styles from '@components/carePlan/ExpertCarePlan.module.css';
import GoalEditorTextField from '@components/carePlan/GoalEditorTextField';
import GoalsManagerExpertTask from '@components/carePlan/GoalsManagerExpertTask';
import GoalsManagerMedia from '@components/carePlan/GoalsManagerMedia';
import GoalsManagerMemberTasks from '@components/carePlan/GoalsManagerMemberTasks';
import GoalsManagerTools from '@components/carePlan/GoalsManagerTools';
import Heading2 from '@components/typography/Heading2';
import useOpener from '@hooks/useOpener';
import { GetJourneyWithPlanQuery } from '@typing/Generated';
import { goalHasDraft } from '@utils/modelUtils';

type Journey = GetJourneyWithPlanQuery['journey'];
type GoalType = Journey['goals'][0];

type Props = {
  currentSection: Section;
  goals: GoalType[];
  journey: Journey;
  setCurrentSection: (section: Section) => void;
};

export enum Section {
  ALL = 'all',
  CARE_PARTNER_TASKS = 'carePartnerTasks',
  MEDIA = 'media',
  MEMBER_TASKS = 'memberTasks',
  TOOLS = 'tools'
}

type GoalProps = {
  currentSection: Section;
  goal: GoalType;
  journey: Journey;
};

const Goal = ({ currentSection, goal, journey }: GoalProps) => {
  const titleOpener = useOpener();

  const isSelectedSection = (section: Section) => currentSection === section || currentSection === Section.ALL;

  return (
    <div className={styles.goalEditor}>
      {titleOpener.isOpen ? (
        <div>
          <GoalEditorTextField
            canTemplate={false}
            defaultValue={goal?.title}
            goal={goal}
            itemBrowserOpener={null}
            journey={journey}
            onCancel={titleOpener.close}
            onSuccess={() => {
              titleOpener.close();
            }}
            templatePlaceholderKey="pages.carePlan.base.goals.searchPlaceholder"
          />
        </div>
      ) : (
        <div className={goal.draft ? styles.goalEditorEditDraft : styles.goalEditorEdit}>
          <Heading2 color={goal.draft ? 'neutral300' : 'shadesWhite'}>{goal.title}</Heading2>
          <ActionIconButton
            ariaLabelKey="accessibility.ariaLabels.edit"
            color={goal.draft ? 'neutral500' : 'shadesWhite'}
            icon="edit"
            onClick={titleOpener.open}
          />
        </div>
      )}

      {isSelectedSection(Section.CARE_PARTNER_TASKS) && (
        <GoalsManagerExpertTask currentSection={currentSection} goal={goal} journey={journey} />
      )}
      {isSelectedSection(Section.MEMBER_TASKS) && (
        <GoalsManagerMemberTasks currentSection={currentSection} goal={goal} journey={journey} />
      )}
      {isSelectedSection(Section.MEDIA) && (
        <GoalsManagerMedia currentSection={currentSection} goal={goal} journey={journey} />
      )}
      {isSelectedSection(Section.TOOLS) && (
        <GoalsManagerTools currentSection={currentSection} goal={goal} journey={journey} />
      )}
    </div>
  );
};

const GoalsManager = ({ currentSection, goals, journey, setCurrentSection }: Props) => {
  const sectionHasDraftItems = (section: Section) => {
    if (section === Section.ALL && goals.length === 1) {
      return goalHasDraft(goals[0]);
    }
    if (section === Section.CARE_PARTNER_TASKS) {
      return goals.some(goal =>
        goal.tasks.filter(t => !t.archived).some(task => task.draft || task.resources.some(resource => resource.draft))
      );
    }

    if (section === Section.MEMBER_TASKS) {
      return goals.some(goal => goal.carePlanActionItems.filter(ai => !ai.archived).some(ai => ai.draft));
    }

    return false;
  };

  useEffect(() => {
    if (goals.length > 1 && currentSection === Section.ALL) {
      setCurrentSection(Section.CARE_PARTNER_TASKS);
    }
  }, [currentSection, goals.length, setCurrentSection]);

  const segmentButtons: ButtonProps<Section> = [
    Section.ALL,
    Section.CARE_PARTNER_TASKS,
    Section.MEMBER_TASKS,
    Section.MEDIA,
    Section.TOOLS
  ].map(section => ({
    disabled: section === Section.ALL && goals.length > 1,
    icon: sectionHasDraftItems(section) ? { color: 'error300', name: 'loader' } : undefined,
    maxWidth:
      section === Section.CARE_PARTNER_TASKS
        ? 167
        : section === Section.MEMBER_TASKS
        ? 142
        : section === Section.ALL
        ? 115
        : section === Section.MEDIA
        ? 80
        : 70,
    textKey: `models.goal.sections.${section}`,
    value: section
  }));

  return (
    <div>
      <Segment
        buttons={segmentButtons}
        fullWidth
        onChange={v => {
          setCurrentSection(v);
        }}
        value={currentSection}
      />
      <div className={styles.goalManager}>
        {goals.map(goal => (
          <Goal currentSection={currentSection} goal={goal} journey={journey} key={goal.id} />
        ))}
      </div>
    </div>
  );
};

export default GoalsManager;
